import gql from 'graphql-tag';
import z from 'zod';

import client from 'src/data/api';
import { CriteriaGroupRecord } from 'src/types';
import { criteriaGroupSchema } from './validation';

type FormValues = z.infer<typeof criteriaGroupSchema>;

export const createCriteriaGroup = (values: FormValues) =>
  client.mutate<{ insert_criteria_groups_one: { id: string } }>({
    mutation: gql`
      mutation insert_criteria_groups_one($criteriaGroupParams: criteria_groups_insert_input!) {
        insert_criteria_groups_one(object: $criteriaGroupParams) {
          id
        }
      }
    `,
    variables: {
      criteriaGroupParams: {
        name: values.name,
        criteria: {
          data: values.criteria_transformed.map(({ id }) => ({ criterion_id: id })),
        },
      },
    },
    errorPolicy: 'all',
  });

export const updateCriteriaGroup = async (
  criteriaGroupParams: Record<string, unknown>,
  criteriaUpdates: FormValues['criteria_transformed'],
  record: CriteriaGroupRecord,
) => {
  const oldCriteriaIdSet = new Set(record.criteria.map(({ criterion }) => criterion.id));

  // Criteria to insert (in form but not in record)
  const criteriaToInsert = criteriaUpdates
    .filter(({ id }) => !oldCriteriaIdSet.has(id))
    .map(({ id }) => ({
      criteria_group_id: record.id,
      criterion_id: id,
      position: criteriaUpdates.findIndex((c) => c.id === id) + 1,
    }));

  // Criteria to update position (exists in both, but position changed)
  const oldCriteriaPositionsByIdMap: Record<string, number> = {};
  for (const { criterion, position } of record.criteria) {
    oldCriteriaPositionsByIdMap[criterion.id] = position;
  }

  const mappingUpdateParamsArr: { criterionID: string; position: number }[] = [];
  criteriaUpdates.forEach(({ id }, idx) => {
    // `position` indexing should be 1-based
    const newPosition = idx + 1;
    // Only update if it exists in old record and position changed
    if (oldCriteriaIdSet.has(id) && oldCriteriaPositionsByIdMap[id] !== newPosition) {
      mappingUpdateParamsArr.push({ criterionID: id, position: newPosition });
    }
  });

  return await client.mutate({
    mutation: gql`
      mutation updateCriteriaGroup(
        $criteriaGroupID: uuid!
        $criteriaGroupParams: criteria_groups_set_input!
        $mappingUpdates: [criteria_group_mappings_updates!]!
        $mappingsToInsert: [criteria_group_mappings_insert_input!]!
      ) {
        update_criteria_groups(where: { id: { _eq: $criteriaGroupID } }, _set: $criteriaGroupParams) {
          affected_rows
        }
        update_criteria_group_mappings_many(updates: $mappingUpdates) {
          affected_rows
        }
        insert_criteria_group_mappings(objects: $mappingsToInsert) {
          affected_rows
        }
      }
    `,
    variables: {
      criteriaGroupID: record.id,
      criteriaGroupParams,
      mappingUpdates: mappingUpdateParamsArr.map(({ criterionID, position }) => ({
        where: { criteria_group_id: { _eq: record.id }, criterion_id: { _eq: criterionID } },
        _set: { position },
      })),
      mappingsToInsert: criteriaToInsert,
    },
    errorPolicy: 'all',
  });
};
